import React, { useState, useEffect, useRef } from 'react';
import { postMoodWithProgress } from '../api/moodApi';
import ProgressBar from './ProgressBar';

// Sound Wave Animation Component with Real Audio Input
function SoundWaveAnimation({ audioLevels = [0, 0, 0, 0, 0, 0, 0, 0] }) {
  return (
    <div style={styles.soundWaveContainer}>
      <div style={styles.soundWave}>
        {audioLevels.map((level, index) => (
          <div 
            key={index}
            style={{
              ...styles.waveBar,
              height: `${Math.max(6, level * 30)}px`,
              opacity: Math.max(0.4, level),
            }}
          />
        ))}
      </div>
    </div>
  );
}

// Companion Message Component with Typing Animation
function CompanionMessage({ text }) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  // Warm, personal signatures with handwritten-style emojis
  const signatures = [
    "— your MoodTune friend ☺",
    "— with love, your music companion ♥",
    "— your understanding friend ✨",
    "— your musical soulmate ❤︎",
    "— your vibe curator ❀",
    "— your emotional DJ ♫",
    "— always here with a smile ☀",
    "— your gentle companion ♡",
    "— in harmony with you ♪",
    "— your steady note of warmth 🤍",
    "— a spark of music ✦",
    "— your guiding melody 🌟",
    "— your little tune of joy ✿",
    "— keeping the rhythm with you ♫",
    "— a friendly chord ☺",
    "— your soft echo of care 🌸",
    "— your simple smile ❤︎",
    "— your heartfelt note ❀",
    "— in tune with your feelings ✨"
  ];
  
  const [signature] = useState(signatures[Math.floor(Math.random() * signatures.length)]);
  
  // Combine text and signature for continuous typing, with quotes around the main text
  const fullMessage = '"' + text + '"' + '\n\n' + signature;

  useEffect(() => {
    if (!text) return;
    
    setDisplayedText('');
    setIsTyping(true);
    
    let currentIndex = 0;
    const typingSpeed = 40; // slower typing for more natural reading pace
    
    const typeText = () => {
      if (currentIndex < fullMessage.length) {
        setDisplayedText(fullMessage.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(typeText, typingSpeed);
      } else {
        setIsTyping(false);
      }
    };
    
    // Start typing after a short delay
    const startDelay = setTimeout(typeText, 600);
    
    return () => clearTimeout(startDelay);
  }, [text, fullMessage]);

  // Split the displayed text to handle different alignments
  const textParts = displayedText.split('\n\n');
  const mainText = textParts[0] || '';
  const signatureText = textParts[1] || '';

  return (
    <div style={companionStyles.companionBox}>
      <div style={companionStyles.companionContent}>
        <div style={companionStyles.companionText} className="companion-text">
          {mainText}
          {signatureText && (
            <>
              {'\n\n'}
              <span style={companionStyles.signatureText}>
                {signatureText}
              </span>
            </>
          )}
          {isTyping && <span style={companionStyles.cursor}>|</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * MoodForm Component
 * 
 * A comprehensive form component that allows users to:
 * - Input their mood via text or voice
 * - Submit mood for AI analysis and song recommendations
 * - Create Spotify playlists from the recommendations
 * 
 * @param {Function} onMoodFetched - Callback when mood is processed and tracks are received
 * @param {Function} setShowCreateButton - Function to show/hide playlist creation button
 * @param {string} suggestedMood - Pre-filled mood suggestion (optional)
 * @param {Function} onCompanionShownEarly - Callback when companion comment is shown early
 * @param {Function} onClearPreviousData - Callback to clear previous request data
 */
export default function MoodForm({ onMoodFetched, setShowCreateButton, suggestedMood, onCompanionShownEarly, onClearPreviousData }) {
  // Form state
  const [mood, setMood] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  
  // Voice recognition state
  const [listening, setListening] = useState(false);
  const [audioLevels, setAudioLevels] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Progress bar state
  const [currentStage, setCurrentStage] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  
  // Early companion comment state
  const [earlyCompanion, setEarlyCompanion] = useState(null);
  
  // Progress bar stages
  const progressStages = [
    {
      id: 'analyzing',
      label: 'Analyzing Mood',
      description: 'Interpreting your story and emotional palette...'
    },
    {
      id: 'pooling',
      label: 'Building Playlist Pools',
      description: 'Searching Spotify and building artist-specific pools...'
    },
    {
      id: 'curating',
      label: 'Curating Your Mix',
      description: 'Selecting the perfect songs from each pool...'
    },
    {
      id: 'finalizing',
      label: 'Final Touches',
      description: 'Saving and preparing your personalized recommendations...'
    }
  ];

  /**
   * Initialize suggested mood if provided
   */
  useEffect(() => {
    if (suggestedMood) {
      setMood(suggestedMood);
    }
  }, [suggestedMood]);

  /**
   * Initialize speech recognition capabilities
   * Sets up Web Speech API for voice input
   */
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setMood((prev) => prev + (prev ? ' ' : '') + transcript);
        setListening(false);
      };
      
      recognition.onend = () => {
        setListening(false);
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setListening(false);
        if (event.error === 'not-allowed') {
          alert('Microphone access denied. Please allow microphone access and try again.');
        } else if (event.error === 'no-speech') {
          alert('No speech detected. Please try again.');
        } else if (event.error === 'audio-capture') {
          alert('No microphone found. Please check your microphone and try again.');
        }
      };
      recognitionRef.current = recognition;
    }
  }, []);

  // Cleanup audio analysis on unmount
  useEffect(() => {
    return () => {
      stopAudioAnalysis();
    };
  }, []);

  // Audio analysis setup
  const setupAudioAnalysis = async () => {
    try {
      // Clean up any existing audio context first
      if (audioContextRef.current) {
        stopAudioAnalysis();
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3; // Less smoothing for more responsive animation
      
      microphone.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      microphoneRef.current = microphone;
      streamRef.current = stream;
      
      startAudioAnalysis();
    } catch (error) {
      console.error('Error setting up audio analysis:', error);
      // Fallback to basic animation if audio access fails
      startFallbackAnimation();
    }
  };

  const startAudioAnalysis = () => {
    if (!analyserRef.current) return;
    
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const updateAudioLevels = () => {
      analyser.getByteFrequencyData(dataArray);
      
      // Create 8 audio level buckets from the frequency data
      // Focus more on lower frequencies (speech range) for better sensitivity
      const bucketSize = Math.floor(bufferLength / 8);
      const newLevels = [];
      
      for (let i = 0; i < 8; i++) {
        let sum = 0;
        for (let j = 0; j < bucketSize; j++) {
          sum += dataArray[i * bucketSize + j];
        }
        const average = sum / bucketSize;
        
        // Apply frequency weighting - lower frequencies (speech) get higher sensitivity
        const frequencyWeight = i < 3 ? 1.5 : 1.0; // Boost first 3 buckets (lower frequencies)
        const amplifiedLevel = Math.min(1, (average / 255) * 4 * frequencyWeight + 0.15);
        newLevels.push(amplifiedLevel);
      }
      
      setAudioLevels(newLevels);
      animationFrameRef.current = requestAnimationFrame(updateAudioLevels);
    };
    
    updateAudioLevels();
  };

  const startFallbackAnimation = () => {
    let time = 0;
    const updateFallbackLevels = () => {
      const newLevels = [];
      for (let i = 0; i < 8; i++) {
        // Create a more dramatic wave-like animation with different phases for each bar
        const phase = (time + i * 0.4) * 0.15;
        const level = Math.abs(Math.sin(phase)) * 0.6 + 0.2; // Range 0.2 to 0.8
        newLevels.push(level);
      }
      setAudioLevels(newLevels);
      time += 0.12;
      animationFrameRef.current = requestAnimationFrame(updateFallbackLevels);
    };
    updateFallbackLevels();
  };

  const stopAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (error) {
        console.warn('AudioContext already closed or error closing:', error);
      }
      audioContextRef.current = null;
    }
    
    if (analyserRef.current) {
      analyserRef.current = null;
    }
    
    setAudioLevels([0, 0, 0, 0, 0, 0, 0, 0]);
  };

  /**
   * Handle microphone button click
   * Toggles voice recognition on/off
   */
  const handleMicClick = async () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }
    
    if (listening) {
      // Stop recognition and audio analysis
      recognitionRef.current.stop();
      setListening(false);
      stopAudioAnalysis();
    } else {
      // Start recognition and audio analysis
      try {
        await setupAudioAnalysis();
        recognitionRef.current.start();
        setListening(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setListening(false);
        stopAudioAnalysis();
        alert('Unable to start voice recognition. Please try again.');
      }
    }
  };

  /**
   * Handle form submission
   * Sends mood to backend for AI analysis and song recommendations
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mood.trim()) {
      setError('Please tell me how yoou feel so that I can find the perfect songs for you.');
      return;
    }
    
    // Clear all previous request data
    if (onClearPreviousData) {
      onClearPreviousData();
    }
    
    setLoading(true);
    setError(null);
    setShowCreateButton(false);
    setCurrentStage(0);
    setShowProgressBar(true);
    setEarlyCompanion(null); // Reset early companion
    
    try {
      // Use the streaming API with real-time progress updates
      const result = await postMoodWithProgress(mood.trim(), (stage, description) => {
        if (stage === 'mood_analysis') {
          // Handle early mood analysis result
          setEarlyCompanion(description.companion);
          // Notify parent that companion was shown early
          if (onCompanionShownEarly) {
            onCompanionShownEarly();
          }
        } else if (typeof stage === 'number') {
          const clampedStage = Math.max(0, Math.min(stage, progressStages.length - 1));
          setCurrentStage(clampedStage);
        }
      });
      
      onMoodFetched(result);
      setShowCreateButton(true);
    } catch (error) {
      setError(error.message || 'Failed to fetch tracks, please try again.');
    } finally {
      setLoading(false);
      setShowProgressBar(false);
      setCurrentStage(0);
      // Keep early companion visible - don't clear it
    }
  };


  return (
    <>
      {/* CSS Animations and Font Import for Companion */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');
          
          .companion-text {
            font-family: "Caveat", cursive !important;
            font-weight: 500 !important;
          }
          
          @keyframes gentleBounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-3px); }
          }
          
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          @keyframes soundWave {
            0%, 100% { 
              height: 4px;
              opacity: 0.4;
            }
            50% { 
              height: 20px;
              opacity: 1;
            }
          }
          
          @keyframes errorSlideIn {
            0% { 
              opacity: 0;
              transform: translateY(-10px);
            }
            100% { 
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Desktop layout - consistent with mobile */
          @media (min-width: 769px) {
            .mood-form-input-group {
              flex-direction: row !important;
              align-items: center !important;
              gap: 0.5rem !important;
            }
            .mood-form-input {
              flex: 1 !important;
            }
            .mood-form-voice-button {
              width: 40px !important;
              height: 40px !important;
              padding: 0 !important;
              border: none !important;
              border-radius: 8px !important;
              margin: 0 !important;
            }
            .mood-form-voice-button svg {
              width: 18px !important;
              height: 18px !important;
            }
            .mood-form-voice-text {
              display: none !important;
            }
            .mood-form-submit-button {
              width: auto !important;
              max-width: 200px !important;
              margin: 0.75rem 0 0 0 !important;
              align-self: center !important;
            }
          }

          /* Responsive font sizing and layout for MoodForm */
          @media (max-width: 768px) {
            .mood-form-input {
              font-size: 0.9rem !important;
            }
            .mood-form-button {
              font-size: 0.9rem !important;
            }
            .mood-form-primary-btn {
              font-size: 0.9rem !important;
            }
            .companion-text {
              font-size: 1.3rem !important;
            }
            .error-title {
              font-size: 13px !important;
            }
            .error-message {
              font-size: 13px !important;
            }
            
            /* Mobile layout changes */
            .mood-form-input-group {
              flex-direction: row !important;
              align-items: center !important;
              gap: 0.5rem !important;
            }
            .mood-form-input {
              flex: 1 !important;
              margin-bottom: 0 !important;
            }
            .mood-form-voice-button {
              width: 40px !important;
              height: 40px !important;
              padding: 0 !important;
              border: none !important;
              border-radius: 8px !important;
              margin: 0 !important;
              position: static !important;
              z-index: auto !important;
            }
            .mood-form-voice-button svg {
              width: 18px !important;
              height: 18px !important;
            }
            .mood-form-voice-text {
              display: none !important;
            }
            .mood-form-submit-button {
              width: 60% !important;
              max-width: 300px !important;
              margin: 0.75rem auto 0 auto !important;
            }
          }

          @media (max-width: 480px) {
            .mood-form-input {
              font-size: 0.85rem !important;
            }
            .mood-form-button {
              font-size: 0.85rem !important;
            }
            .mood-form-primary-btn {
              font-size: 0.85rem !important;
            }
            .companion-text {
              font-size: 1.1rem !important;
            }
            .error-title {
              font-size: 12px !important;
            }
            .error-message {
              font-size: 12px !important;
            }
          }
        `}
      </style>
      
      <form onSubmit={handleSubmit} style={styles.form}>
      {/* Mood Input Group */}
      <div style={styles.inputGroup} className="mood-form-input-group">
        <input
          type="text"
          placeholder="Describe your mood..."
          value={mood}
          onChange={(e) => setMood(e.target.value)}
          disabled={loading}
          style={styles.input}
          className="mood-form-input"
        />
        
        {/* Voice Input Button with Sound Wave Animation */}
        <button
          type="button"
          onClick={handleMicClick}
          style={{
            backgroundColor: listening ? '#f87171' : '#e0e7ff',
            color: listening ? 'white' : '#4f46e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          className="mood-form-button mood-form-voice-button"
        >
            {listening ? (
              <>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  fill="currentColor" 
                  viewBox="0 0 16 16"
                >
                  <path d="M12.734 9.613A4.995 4.995 0 0 0 13 8V7a.5.5 0 0 0-1 0v1c0 .274-.027.54-.08.799l.814.814zm-2.522 1.72A4 4 0 0 1 4 8V7a.5.5 0 0 0-1 0v1a5 5 0 0 0 4.5 4.975V15h-3a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1h-3v-2.025a4.973 4.973 0 0 0 2.43-.923l-.718-.719zM11 7.88V3a3 3 0 0 0-5.842-.963L11 7.879zM5 6.12l4.486 4.486A3 3 0 0 1 5 8V6.121zm8.646 7.234-12-12 .708-.708 12 12-.708.707z"/>
                </svg>
                <span className="mood-form-voice-text">Listening...</span>
              </>
            ) : (
              <>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  fill="currentColor" 
                  viewBox="0 0 16 16"
                >
                  <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z"/>
                  <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/>
                </svg>
                <span className="mood-form-voice-text">Speak</span>
              </>
            )}
          </button>
          
          {/* Sound Wave Animation - Only show when listening */}
          {listening && <SoundWaveAnimation audioLevels={audioLevels} />}
      </div>
      
      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        style={{
          ...styles.primaryBtn,
          backgroundColor: loading ? '#a5b4fc' : '#6366f1',
          color: 'white',
        }}
        className="mood-form-primary-btn mood-form-submit-button"
      >
        {loading ? (
          <>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="currentColor" 
              viewBox="0 0 16 16"
              style={{ marginRight: '8px' }}
              className="spinner"
            >
              <path d="M8 3a5 5 0 1 0 5 5H8V3z"/>
            </svg>
            Processing...
          </>
        ) : (
          'Get Tracks'
        )}
      </button>

      {/* Error Display */}
      {error && (
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
          </div>
          <div style={styles.errorContent}>
            <div style={styles.errorTitle} className="error-title">Oops!</div>
            <div style={styles.errorMessage} className="error-message">{error}</div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <ProgressBar 
        stages={progressStages}
        currentStage={currentStage}
        isVisible={showProgressBar}
      />

      {/* Early Companion Comment */}
      {earlyCompanion && (
        <CompanionMessage text={earlyCompanion} />
      )}

      </form>
    </>
  );
}

// Component styles
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    borderRadius: '12px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    minWidth: '250px',
  },
  iconButton: {
    padding: '0.7rem 1.25rem',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  primaryBtn: {
    padding: '0.75rem 1.25rem',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '1rem',
  },
  result: {
    marginTop: '1rem',
    textAlign: 'center',
    fontSize: '1rem',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginTop: '1rem',
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)',
    animation: 'errorSlideIn 0.3s ease-out',
  },
  errorIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    color: '#dc2626',
    flexShrink: 0,
    marginTop: '2px',
  },
  errorContent: {
    flex: 1,
    minWidth: 0,
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '4px',
    lineHeight: 1.2,
  },
  errorMessage: {
    fontSize: '14px',
    color: '#991b1b',
    lineHeight: 1.4,
    fontWeight: '500',
  },
  // Sound Wave Animation Styles
  voiceInputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  soundWaveContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  soundWave: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    height: '20px',
  },
  waveBar: {
    width: '3px',
    backgroundColor: '#f87171',
    borderRadius: '2px',
    transition: 'height 0.1s ease-out, opacity 0.1s ease-out',
  },
};

// Companion Message Styles
const companionStyles = {
  companionBox: {
    background: '#FDF6F0',
    border: '2px solid #f3e8d3',
    borderRadius: '20px',
    padding: '2rem',
    marginTop: '1rem',
    position: 'relative',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
    }
  },
  companionContent: {
    position: 'relative',
  },
  companionText: {
    fontSize: '1.5rem',
    color: '#451a03',
    lineHeight: 1.5,
    margin: 0,
    fontFamily: '"Caveat", cursive, "Brush Script MT", sans-serif',
    fontWeight: 500,
    letterSpacing: '0.01em',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    whiteSpace: 'pre-line',
    fontDisplay: 'swap',
  },
  quoteMark: {
    fontSize: '2rem',
    color: '#451a03',
    fontWeight: 'bold',
    fontFamily: '"Caveat", cursive, "Brush Script MT", sans-serif',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    margin: '0 0.2rem',
  },
  signatureText: {
    display: 'block',
    textAlign: 'right',
    marginTop: '0.5rem',
  },
  cursor: {
    color: '#f59e0b',
    fontWeight: 'bold',
    animation: 'blink 1s infinite',
  },
};
